import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import type { ChecklistTemplate } from "@/types/components";
import type { TemplateChecklist } from "./useTemplateBuilderState";

const apiUrl = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8788";

interface UseTemplateBuilderMutationsProps {
  templateId?: number | null;
  templateChecklists: TemplateChecklist[];
  onClose: () => void;
}

export function useTemplateBuilderMutations({
  templateId,
  templateChecklists,
  onClose,
}: UseTemplateBuilderMutationsProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Save template mutation
  const saveTemplateMutation = useMutation({
    mutationFn: async (templateToSave: ChecklistTemplate) => {
      const url = templateId
        ? `${apiUrl}/api/v1/templates/${templateId}`
        : `${apiUrl}/api/v1/templates`;
      const method = templateId ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...templateToSave,
          template_data: { checklists: templateChecklists },
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save template");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      toast({
        title: "Template Saved",
        description: "Template has been saved successfully.",
      });
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save template",
        variant: "destructive",
      });
    },
  });

  const handleSaveTemplate = (template: ChecklistTemplate | null) => {
    if (!(template && template.name.trim())) {
      toast({
        title: "Name Required",
        description: "Please enter a template name.",
        variant: "destructive",
      });
      return;
    }

    saveTemplateMutation.mutate(template);
  };

  return {
    saveTemplateMutation,
    handleSaveTemplate,
    isSaving: saveTemplateMutation.isPending,
  };
}
